import { _js } from '@ifixit/localize';
import { useEffect, useState, useRef, forwardRef } from 'react';
import type { ForwardedRef, ChangeEvent, KeyboardEvent } from 'react';
import {
   FormControl,
   FormLabel,
   Input,
   Button,
   Stack,
   ButtonGroup,
   Popover,
   PopoverTrigger,
   IconButton,
   PopoverContent,
   PopoverArrow,
   useDisclosure,
   Tooltip,
   Box,
} from '@chakra-ui/react';
import FocusLock from 'react-focus-lock';
import { faVideo } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { tiptapButtonStyle } from './TiptapButton';
import { shadow } from '@ifixit/primitives';
import { useEditorContext } from '../editor_context';

interface FieldProps {
   id: string;
   label: string;
   value: string;
   onChange: (event: ChangeEvent<HTMLInputElement>) => void;
   handleKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
}

const VideoInsertTextInput = forwardRef(
   (
      { id, label, value, onChange, handleKeyDown }: FieldProps,
      ref: ForwardedRef<HTMLInputElement>
   ) => (
      <FormControl>
         <FormLabel htmlFor={id}>{label}</FormLabel>
         <Input
            id={id}
            value={value}
            onChange={onChange}
            onKeyDown={handleKeyDown}
            ref={ref}
            bgColor="white"
            fontSize="sm"
         />
      </FormControl>
   )
);
VideoInsertTextInput.displayName = 'VideoInsertTextInput';

const VideoInsertForm = ({
   isOpen,
   onClose,
   urlFieldRef,
}: {
   isOpen: boolean;
   onClose: () => void;
   urlFieldRef: ForwardedRef<HTMLInputElement>;
}) => {
   const [url, setUrl] = useState('');
   const [caption, setCaption] = useState('');
   const { editor } = useEditorContext();

   useEffect(() => {
      if (isOpen) {
         setUrl('');
         setCaption('');
      }
   }, [isOpen]);

   const onSubmit = () => {
      editor.chain().focus().insertVideoContent(url, caption).run();
      onClose();
   };

   const onCancel = () => {
      editor.commands.focus();
      onClose();
   };

   const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter') {
         return;
      }

      event.preventDefault();

      if (url) {
         onSubmit();
      }
   };

   const handleUrlChange = (event: ChangeEvent<HTMLInputElement>) => {
      setUrl(event.currentTarget.value);
   };

   const handleCaptionChange = (event: ChangeEvent<HTMLInputElement>) => {
      setCaption(event.currentTarget.value);
   };

   const buttonStyle = {
      margin: '2',
      marginTop: '4',
      marginRight: '0',
   };

   return (
      <Stack>
         <VideoInsertTextInput
            id="video-url"
            label={_js('YouTube or Vimeo Video URL')}
            value={url}
            onChange={handleUrlChange}
            handleKeyDown={handleKeyDown}
            ref={urlFieldRef}
         />
         <VideoInsertTextInput
            id="video-caption"
            label={_js('Video Caption (optional)')}
            value={caption}
            onChange={handleCaptionChange}
            handleKeyDown={handleKeyDown}
         />
         <ButtonGroup display="flex" justifyContent="flex-end">
            <Button onClick={onCancel} {...buttonStyle}>
               {_js('Cancel')}
            </Button>
            <Button
               onClick={onSubmit}
               isDisabled={!url}
               {...buttonStyle}
               variant="solid"
               colorScheme="blue"
            >
               {_js('Insert Video')}
            </Button>
         </ButtonGroup>
      </Stack>
   );
};

export const VideoInsertDropdown = () => {
   const { onOpen, onClose, isOpen } = useDisclosure();
   const urlFieldRef = useRef<HTMLInputElement>(null);
   const { editor } = useEditorContext();

   return (
      <>
         <Popover
            returnFocusOnClose={false}
            isOpen={isOpen}
            initialFocusRef={urlFieldRef}
            onOpen={onOpen}
            onClose={onClose}
            placement="bottom"
         >
            <Tooltip placement="top-start" label={_js('Insert a YouTube or Vimeo Video')}>
               <Box>
                  <PopoverTrigger>
                     <IconButton
                        aria-label={_js('Insert a YouTube or Vimeo Video')}
                        icon={<FontAwesomeIcon icon={faVideo} />}
                        isDisabled={!editor.state.selection.empty}
                        isActive={isOpen}
                        {...tiptapButtonStyle}
                     />
                  </PopoverTrigger>
               </Box>
            </Tooltip>
            <PopoverContent
               rootProps={{ width: '60%' }}
               bgColor="gray.50"
               border="1px"
               borderColor="gray.300"
               borderRadius="md"
               padding="2.5"
               boxShadow={shadow[3]}
               width={'100%'}
            >
               <FocusLock persistentFocus={false}>
                  <PopoverArrow />
                  <VideoInsertForm isOpen={isOpen} onClose={onClose} urlFieldRef={urlFieldRef} />
               </FocusLock>
            </PopoverContent>
         </Popover>
      </>
   );
};
